export interface EntityPose {
  x: number;
  y: number;
  facing: number;
}

interface Snapshot {
  t: number;
  players: Map<string, EntityPose>;
  enemies: Map<number, EntityPose>;
}

function lerp(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

function lerpAngle(a: number, b: number, k: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * k;
}

function lerpPose(a: EntityPose, b: EntityPose, k: number): EntityPose {
  return {
    x: lerp(a.x, b.x, k),
    y: lerp(a.y, b.y, k),
    facing: lerpAngle(a.facing, b.facing, k),
  };
}

/**
 * Ring buffer of server snapshots. Remote entities are rendered at
 * `now - renderDelay`, interpolating between the two bracketing snapshots so
 * motion is smooth even though state arrives at ~20 Hz. No extrapolation, so a
 * late packet never causes a snap-back — it just briefly holds the last pose.
 */
export class SnapshotBuffer {
  private buf: Snapshot[] = [];
  private readonly maxAgeMs = 2000;

  push(players: Map<string, EntityPose>, enemies: Map<number, EntityPose>): void {
    const now = performance.now();
    this.buf.push({ t: now, players, enemies });
    const cutoff = now - this.maxAgeMs;
    while (this.buf.length > 2 && this.buf[0]!.t < cutoff) this.buf.shift();
  }

  private bracket(renderTime: number): [Snapshot, Snapshot, number] | null {
    if (this.buf.length === 0) return null;
    if (this.buf.length === 1) return [this.buf[0]!, this.buf[0]!, 0];

    for (let i = this.buf.length - 1; i > 0; i--) {
      const a = this.buf[i - 1]!;
      const b = this.buf[i]!;
      if (a.t <= renderTime && renderTime <= b.t) {
        const span = b.t - a.t || 1;
        return [a, b, (renderTime - a.t) / span];
      }
    }
    if (renderTime < this.buf[0]!.t) return [this.buf[0]!, this.buf[0]!, 0];
    const last = this.buf[this.buf.length - 1]!;
    return [last, last, 0];
  }

  playerPose(id: string, renderDelayMs: number): EntityPose | null {
    const br = this.bracket(performance.now() - renderDelayMs);
    if (!br) return null;
    const [a, b, k] = br;
    const pa = a.players.get(id);
    const pb = b.players.get(id);
    if (pa && pb) return lerpPose(pa, pb, k);
    return pb ?? pa ?? null;
  }

  enemyPose(id: number, renderDelayMs: number): EntityPose | null {
    const br = this.bracket(performance.now() - renderDelayMs);
    if (!br) return null;
    const [a, b, k] = br;
    const ea = a.enemies.get(id);
    const eb = b.enemies.get(id);
    if (ea && eb) return lerpPose(ea, eb, k);
    return eb ?? ea ?? null;
  }
}
